// In AcademicSelector.jsx - Complete fixed version

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiFilter, FiAlertCircle, FiCheckCircle, FiRefreshCw, FiLayers, FiBook, FiCalendar, FiClock } from 'react-icons/fi';
import api from '../../api';

const AcademicSelector = ({
  tenantId: propTenantId,
  academicYears: propAcademicYears,
  completedAcademicYears: propCompletedAcademicYears,
  selectedAcademicYear: propSelectedAcademicYear,
  setSelectedAcademicYear: propSetSelectedAcademicYear,
  isSemesterBased: propIsSemesterBased,
  semesters: propSemesters,
  completedSemesters: propCompletedSemesters,
  terms: propTerms,
  completedTerms: propCompletedTerms,
  selectedSemester: propSelectedSemester,
  setSelectedSemester: propSetSelectedSemester,
  selectedTerm: propSelectedTerm,
  setSelectedTerm: propSetSelectedTerm,
  activeTab: propActiveTab,
  setActiveTab: propSetActiveTab,
  includeCompleted = true,
  onCreateClass,
  loading: propLoading = false,
  isViewOnly: propIsViewOnly = false,
  programTypes: propProgramTypes,
  selectedProgramTypeId: propSelectedProgramTypeId,
  setSelectedProgramTypeId: propSetSelectedProgramTypeId,
  programs: propPrograms,
  selectedProgramId: propSelectedProgramId,
  setSelectedProgramId: propSetSelectedProgramId,
  fetchMappings = false,
  onContextChange,
  onMappingCheck,
  showMappingStatus = false,
  enableReset = true
}) => {
  const tenantId = propTenantId || localStorage.getItem('tenantId');

  // State for internal management
  const [internalSelectedProgramTypeId, setInternalSelectedProgramTypeId] = useState(propSelectedProgramTypeId || '');
  const [internalSelectedProgramId, setInternalSelectedProgramId] = useState(propSelectedProgramId || '');
  const [internalSelectedAcademicYear, setInternalSelectedAcademicYear] = useState(propSelectedAcademicYear || '');
  const [internalSelectedSemester, setInternalSelectedSemester] = useState(propSelectedSemester || '');
  const [internalSelectedTerm, setInternalSelectedTerm] = useState(propSelectedTerm || '');
  const [internalActiveTab, setInternalActiveTab] = useState(propActiveTab || 'active');
  
  // 🔥 FIX: Separate state for system type detection
  const [academicSystemType, setAcademicSystemType] = useState(null); // 'semester', 'term', or null
  const [systemTypeDetected, setSystemTypeDetected] = useState(false);

  // Data States
  const [internalProgramTypes, setInternalProgramTypes] = useState([]);
  const [internalPrograms, setInternalPrograms] = useState([]);
  const [internalAcademicYears, setInternalAcademicYears] = useState([]);
  const [internalCompletedAcademicYears, setInternalCompletedAcademicYears] = useState([]);
  const [internalSemesters, setInternalSemesters] = useState([]);
  const [internalCompletedSemesters, setInternalCompletedSemesters] = useState([]);
  const [internalTerms, setInternalTerms] = useState([]);
  const [internalCompletedTerms, setInternalCompletedTerms] = useState([]);
  
  // Loading states
  const [loadingProgramTypes, setLoadingProgramTypes] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
  const [loadingSemesters, setLoadingSemesters] = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [error, setError] = useState('');
  const [programMapping, setProgramMapping] = useState(null);
  const [hasExistingMapping, setHasExistingMapping] = useState(false);

  // Determine if using internal or prop state
  const useInternalState = !propProgramTypes || !propAcademicYears;

  // Get current values
  const selectedProgramTypeId = useInternalState ? internalSelectedProgramTypeId : propSelectedProgramTypeId;
  const selectedProgramId = useInternalState ? internalSelectedProgramId : propSelectedProgramId;
  const selectedAcademicYear = useInternalState ? internalSelectedAcademicYear : propSelectedAcademicYear;
  const selectedSemester = useInternalState ? internalSelectedSemester : propSelectedSemester;
  const selectedTerm = useInternalState ? internalSelectedTerm : propSelectedTerm;
  const activeTab = useInternalState ? internalActiveTab : propActiveTab;

  // Get the actual data arrays
  const programTypes = useInternalState ? internalProgramTypes : propProgramTypes;
  const programs = useInternalState ? internalPrograms : propPrograms;
  
  // Academic Years
  const academicYears = useInternalState ? internalAcademicYears : propAcademicYears;
  const completedAcademicYears = useInternalState ? internalCompletedAcademicYears : propCompletedAcademicYears;
  const currentAcademicYears = activeTab === 'active' ? academicYears : completedAcademicYears;
  
  // Semesters
  const semesters = useInternalState ? internalSemesters : propSemesters;
  const completedSemesters = useInternalState ? internalCompletedSemesters : propCompletedSemesters;
  const currentSemesters = activeTab === 'active' ? semesters : completedSemesters;
  
  // Terms
  const terms = useInternalState ? internalTerms : propTerms;
  const completedTerms = useInternalState ? internalCompletedTerms : propCompletedTerms;
  const currentTerms = activeTab === 'active' ? terms : completedTerms;

  // 🔥 FIX: Determine if current academic year is semester or term based
  const isSemesterBased = useMemo(() => {
    if (academicSystemType === 'semester') return true;
    if (academicSystemType === 'term') return false;
    
    // Fallback to prop
    if (propIsSemesterBased !== undefined) return propIsSemesterBased;
    
    // Default to false if not detected
    return false;
  }, [academicSystemType, propIsSemesterBased]);

  // Safe setters (unchanged)
  const safeSetSelectedProgramTypeId = useCallback((val) => {
    // Always reset detection on upstream context switches (works for both controlled and internal modes)
    setAcademicSystemType(null);
    setSystemTypeDetected(false);

    if (useInternalState) {
      setInternalSelectedProgramTypeId(val);
      setInternalSelectedProgramId('');
      setInternalSelectedAcademicYear('');
      setInternalSelectedSemester('');
      setInternalSelectedTerm('');
    } else {
      propSetSelectedProgramTypeId?.(val);
      propSetSelectedProgramId?.('');
      propSetSelectedAcademicYear?.('');
      propSetSelectedSemester?.('');
      propSetSelectedTerm?.('');
    }
  }, [useInternalState, propSetSelectedProgramTypeId, propSetSelectedProgramId, propSetSelectedAcademicYear, propSetSelectedSemester, propSetSelectedTerm]);

  const safeSetSelectedProgramId = useCallback((val) => {
    // Always reset detection on upstream context switches
    setAcademicSystemType(null);
    setSystemTypeDetected(false);

    if (useInternalState) {
      setInternalSelectedProgramId(val);
      setInternalSelectedAcademicYear('');
      setInternalSelectedSemester('');
      setInternalSelectedTerm('');
    } else {
      propSetSelectedProgramId?.(val);
      propSetSelectedAcademicYear?.('');
      propSetSelectedSemester?.('');
      propSetSelectedTerm?.('');
    }
  }, [useInternalState, propSetSelectedProgramId, propSetSelectedAcademicYear, propSetSelectedSemester, propSetSelectedTerm]);

  const safeSetSelectedAcademicYear = useCallback((val) => {
    // Always reset and re-detect system type when AY changes
    setAcademicSystemType(null);
    setSystemTypeDetected(false);

    if (useInternalState) {
      setInternalSelectedAcademicYear(val);
      setInternalSelectedSemester('');
      setInternalSelectedTerm('');
    } else {
      propSetSelectedAcademicYear?.(val);
      propSetSelectedSemester?.('');
      propSetSelectedTerm?.('');
    }
  }, [useInternalState, propSetSelectedAcademicYear, propSetSelectedSemester, propSetSelectedTerm]);

  const safeSetSelectedSemester = useCallback((val) => {
    if (useInternalState) {
      setInternalSelectedSemester(val);
    } else {
      propSetSelectedSemester?.(val);
    }
  }, [useInternalState, propSetSelectedSemester]);

  const safeSetSelectedTerm = useCallback((val) => {
    if (useInternalState) {
      setInternalSelectedTerm(val);
    } else {
      propSetSelectedTerm?.(val);
    }
  }, [useInternalState, propSetSelectedTerm]);

  const safeSetActiveTab = useCallback((tab) => {
    setAcademicSystemType(null);
    setSystemTypeDetected(false);

    if (useInternalState) {
      setInternalActiveTab(tab);
      setInternalSelectedAcademicYear('');
      setInternalSelectedSemester('');
      setInternalSelectedTerm('');
    } else {
      propSetActiveTab?.(tab);
    }
  }, [useInternalState, propSetActiveTab]);

  // Fetch program types (unchanged)
  const fetchProgramTypes = useCallback(async () => {
    if (!tenantId || !useInternalState) return;
    try {
      setLoadingProgramTypes(true);
      const res = await api.get('/api/program-types', { 
        params: { tenantId, status: 'active' } 
      });
      const list = res.data.programTypes || [];
      setInternalProgramTypes(list);
    } catch (err) { 
      setError('Failed to load program types'); 
    } finally { 
      setLoadingProgramTypes(false); 
    }
  }, [tenantId, useInternalState]);

  // Fetch programs (unchanged)
  const fetchPrograms = useCallback(async () => {
    if (!tenantId || !selectedProgramTypeId || !useInternalState) return;
    try {
      setLoadingPrograms(true);
      const res = await api.get('/api/programs', { 
        params: { tenantId, programTypeId: selectedProgramTypeId, status: 'active' } 
      });
      const list = res.data.programs || [];
      setInternalPrograms(list);
    } catch (err) { 
      setError('Failed to load programs'); 
    } finally { 
      setLoadingPrograms(false); 
    }
  }, [tenantId, selectedProgramTypeId, useInternalState]);

  // Fetch academic years (unchanged)
  const fetchAcademicYears = useCallback(async () => {
    if (!tenantId || !useInternalState) return;
    try {
      setLoadingAcademicYears(true);
      const endpoint = activeTab === 'active' ? '/api/academic-year/all-active' : '/api/academic-year/all-completed';
      const res = await api.get(endpoint, { params: { tenantId } });
      const years = res.data.academicYears || res.data.activeAcademicYears || [];
      if (activeTab === 'active') {
        setInternalAcademicYears(years);
      } else {
        setInternalCompletedAcademicYears(years);
      }
    } catch (err) { 
      setError(`Failed to load ${activeTab} years`); 
    } finally { 
      setLoadingAcademicYears(false); 
    }
  }, [tenantId, activeTab, useInternalState]);

  // 🔥 FIX: Enhanced semester fetching with type detection
  const fetchSemesters = useCallback(async () => {
    if (!selectedAcademicYear) return;
    
    try {
      setLoadingSemesters(true);
      console.log('📚 Fetching semesters for:', selectedAcademicYear);
      
      const res = await api.get(`/api/classManager/semesters/${selectedAcademicYear}`);
      console.log('📥 Semesters response:', res.data);
      
      const semesterList = res.data.semesters || [];
      
      if (semesterList.length > 0) {
        console.log('✅ This is a SEMESTER-BASED academic year');
        setAcademicSystemType('semester');
        setSystemTypeDetected(true);
        
        if (useInternalState) {
          if (activeTab === 'active') {
            setInternalSemesters(semesterList);
          } else {
            setInternalCompletedSemesters(semesterList);
          }
        }
      } else {
        console.log('ℹ️ No semesters found - might be term-based');
        // Don't set system type yet - wait for terms fetch
      }
      
    } catch (err) {
      console.warn('⚠️ Error fetching semesters:', err.message);
      // Don't set error - this might be a term-based system
    } finally {
      setLoadingSemesters(false);
    }
  }, [selectedAcademicYear, activeTab, useInternalState]);

  // 🔥 FIX: Enhanced term fetching with type detection
  const fetchTerms = useCallback(async () => {
    if (!selectedAcademicYear) return;
    
    try {
      setLoadingTerms(true);
      console.log('📅 Fetching terms for:', selectedAcademicYear);
      
      // Use your existing working endpoint
      const res = await api.get(`/api/academic-year/${selectedAcademicYear}/terms`, {
        params: { tenantId }
      });
      
      console.log('📥 Terms response:', res.data);
      
      const termList = res.data.terms || [];
      
      if (termList.length > 0) {
        console.log('✅ This is a TERM-BASED academic year');
        setAcademicSystemType('term');
        setSystemTypeDetected(true);
        
        // Normalize term data
        const normalizedTerms = termList.map(term => ({
          _id: term._id,
          id: term._id,
          termNumber: term.termNumber,
          termLabel: term.termLabel || `Term ${term.termNumber}`,
          name: term.termLabel || `Term ${term.termNumber}`,
          status: term.status || 'active'
        }));
        
        if (useInternalState) {
          if (activeTab === 'active') {
            setInternalTerms(normalizedTerms);
          } else {
            setInternalCompletedTerms(normalizedTerms);
          }
        }
      } else {
        console.log('ℹ️ No terms found');
      }
      
    } catch (err) {
      console.error('❌ Error fetching terms:', err);
    } finally {
      setLoadingTerms(false);
    }
  }, [selectedAcademicYear, tenantId, activeTab, useInternalState]);

  // 🔥 FIX: Detect system type when academic year is selected
  useEffect(() => {
    if (selectedAcademicYear && !systemTypeDetected) {
      console.log('🔍 Detecting system type for academic year:', selectedAcademicYear);
      
      // Reset system type
      setAcademicSystemType(null);
      
      // Fetch both and let them set the type based on what returns data
      fetchSemesters();
      fetchTerms();
    }
  }, [selectedAcademicYear, systemTypeDetected, fetchSemesters, fetchTerms]);

  // Fetch program mapping (unchanged)
  const fetchProgramMapping = useCallback(async () => {
    if (!tenantId || !selectedProgramId || !selectedProgramTypeId || !selectedAcademicYear || !fetchMappings) {
      return;
    }

    if (isSemesterBased && !selectedSemester) return;
    if (!isSemesterBased && !selectedTerm) return;

    try {
      setLoadingMapping(true);
      const params = { 
        tenantId, 
        programId: selectedProgramId, 
        programTypeId: selectedProgramTypeId, 
        academicYearId: selectedAcademicYear 
      };
      
      if (isSemesterBased && selectedSemester) {
        params.semesterId = selectedSemester;
      } else if (!isSemesterBased && selectedTerm) {
        params.termId = selectedTerm;
      }
      
      const res = await api.get('/api/program-subject-mappings/grouped', { params });
      if (res.data.mapping) {
        setProgramMapping(res.data.mapping);
        setHasExistingMapping(true);
        onMappingCheck?.(res.data.mapping);
      } else {
        setHasExistingMapping(false);
      }
    } catch (err) {
      setHasExistingMapping(false);
    } finally {
      setLoadingMapping(false);
    }
  }, [tenantId, selectedProgramId, selectedProgramTypeId, selectedAcademicYear, 
      selectedSemester, selectedTerm, isSemesterBased, fetchMappings, onMappingCheck]);

  // Effects for initial data (unchanged)
  useEffect(() => { 
    if (useInternalState) fetchProgramTypes(); 
  }, [useInternalState, fetchProgramTypes]);

  useEffect(() => { 
    if (useInternalState && selectedProgramTypeId) fetchPrograms(); 
  }, [useInternalState, selectedProgramTypeId, fetchPrograms]);

  useEffect(() => { 
    if (useInternalState) fetchAcademicYears(); 
  }, [useInternalState, activeTab, fetchAcademicYears]);

  useEffect(() => { 
    if (fetchMappings) fetchProgramMapping(); 
  }, [fetchMappings, fetchProgramMapping]);

  // Auto-select first semester/term
  useEffect(() => {
    if (isSemesterBased && currentSemesters.length > 0 && !selectedSemester) {
      console.log('🎯 Auto-selecting first semester');
      safeSetSelectedSemester(currentSemesters[0]._id);
    }
  }, [isSemesterBased, currentSemesters, selectedSemester, safeSetSelectedSemester]);

  useEffect(() => {
    if (!isSemesterBased && currentTerms.length > 0 && !selectedTerm) {
      console.log('🎯 Auto-selecting first term');
      safeSetSelectedTerm(currentTerms[0]._id || currentTerms[0].id);
    }
  }, [isSemesterBased, currentTerms, selectedTerm, safeSetSelectedTerm]);

  // Notify parent of context changes.
  // Important: emit even before system type is detected so parent can detect it in controlled mode.
  useEffect(() => {
    if (!onContextChange) return;

    onContextChange({
      tenantId,
      programTypeId: selectedProgramTypeId,
      programId: selectedProgramId,
      academicYearId: selectedAcademicYear,
      semesterId: isSemesterBased ? selectedSemester : null,
      termId: !isSemesterBased ? selectedTerm : null,
      isSemesterBased,
      hasExistingMapping,
      programMapping,
      semesters: currentSemesters,
      terms: currentTerms,
      completedSemesters,
      completedTerms,
      systemType: systemTypeDetected ? (isSemesterBased ? 'semester' : 'term') : null
    });
  }, [selectedProgramTypeId, selectedProgramId, selectedAcademicYear,
      selectedSemester, selectedTerm, isSemesterBased, hasExistingMapping,
      programMapping, tenantId, onContextChange, currentSemesters, currentTerms,
      completedSemesters, completedTerms, systemTypeDetected]);

  const resetSelections = () => {
    safeSetSelectedProgramTypeId('');
    safeSetSelectedProgramId('');
    safeSetSelectedAcademicYear('');
    safeSetSelectedSemester('');
    safeSetSelectedTerm('');
    setError('');
    setAcademicSystemType(null);
    setSystemTypeDetected(false);
  };

  const handleTabChange = (tab) => {
    safeSetActiveTab(tab);
  };

  const handleCreateClick = () => {
    if (!onCreateClass) return;
    
    onCreateClass({
      tenantId,
      programTypeId: selectedProgramTypeId,
      programId: selectedProgramId,
      academicYearId: selectedAcademicYear,
      semesterId: isSemesterBased ? selectedSemester : null,
      termId: !isSemesterBased ? selectedTerm : null,
      isSemesterBased,
      hasExistingMapping,
      programMapping
    });
  };

  const filteredPrograms = useMemo(() => {
    if (!selectedProgramTypeId) return programs;
    return programs.filter(p => {
      const programTypeId = typeof p.programTypeId === 'object' ? p.programTypeId._id : p.programTypeId;
      return programTypeId === selectedProgramTypeId;
    });
  }, [programs, selectedProgramTypeId]);

  const isContextComplete = useMemo(() => {
    return !!selectedProgramTypeId && 
           !!selectedProgramId && 
           !!selectedAcademicYear && 
           systemTypeDetected &&
           (!isSemesterBased || (isSemesterBased && !!selectedSemester)) &&
           (isSemesterBased || (!isSemesterBased && !!selectedTerm));
  }, [selectedProgramTypeId, selectedProgramId, selectedAcademicYear, isSemesterBased, selectedSemester, selectedTerm, systemTypeDetected]);

  const loading = propLoading || loadingProgramTypes || loadingPrograms || loadingAcademicYears || 
                  loadingSemesters || loadingTerms || loadingMapping;
  const isViewOnly = propIsViewOnly || (activeTab === 'completed');

  // 🔥 FIX: Show loading while detecting system type
  const showSystemTypeLoading = selectedAcademicYear && !systemTypeDetected && (loadingSemesters || loadingTerms);

  if (!tenantId) {
    return (
      <div className="p-4 bg-red-50 text-red-600 rounded-lg border border-red-200 text-sm flex items-center gap-2">
        <FiAlertCircle /> Tenant ID is missing.
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-600 rounded-lg text-white">
            <FiFilter className="w-4 h-4" />
          </div>
          <h3 className="font-bold text-gray-800">Academic Context</h3>
        </div>

        {includeCompleted && (
          <div className="inline-flex p-1 bg-gray-200 rounded-lg">
            {['active', 'completed'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                disabled={loading}
                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  activeTab === tab ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 items-end">
          
          {/* Program Type Select */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <FiLayers className="w-3 h-3" /> Program Type
            </label>
            <select
              value={selectedProgramTypeId}
              onChange={(e) => safeSetSelectedProgramTypeId(e.target.value)}
              disabled={loading}
              className="w-full text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select Type</option>
              {programTypes.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
            </select>
          </div>

          {/* Program Select */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <FiBook className="w-3 h-3" /> Program
            </label>
            {!selectedProgramTypeId && (
              <div className="text-xs text-gray-400">Select program type first</div>
            )}
            <select
              value={selectedProgramId}
              onChange={(e) => safeSetSelectedProgramId(e.target.value)}
              disabled={loading || !selectedProgramTypeId}
              className="w-full text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select Program</option>
              {filteredPrograms.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </div>

          {/* Academic Year Select */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <FiCalendar className="w-3 h-3" /> Academic Year
            </label>
            {!selectedProgramId && (
              <div className="text-xs text-gray-400">Select program first</div>
            )}
            <select
              value={selectedAcademicYear}
              onChange={(e) => safeSetSelectedAcademicYear(e.target.value)}
              disabled={loading || !selectedProgramId}
              className="w-full text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">Select Year</option>
              {currentAcademicYears.map(y => (
                <option key={y._id} value={y._id}>
                  {y.name} {y.label ? `(${y.label})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* 🔥 FIX: Show loading indicator while detecting system type */}
          {showSystemTypeLoading && (
            <div className="col-span-1 flex items-center space-x-2 text-blue-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-xs">Detecting system type...</span>
            </div>
          )}

          {/* 🔥 FIX: Semester Dropdown - Only show when detected as semester-based */}
          {systemTypeDetected && isSemesterBased && (
            <div className="space-y-1.5 animate-in fade-in slide-in-from-left-2">
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                <FiRefreshCw className="w-3 h-3" /> Semester
              </label>
              {currentSemesters.length === 0 && (
                <div className="text-xs text-amber-600">No semesters found</div>
              )}
              <select
                value={selectedSemester}
                onChange={(e) => safeSetSelectedSemester(e.target.value)}
                disabled={loading || currentSemesters.length === 0}
                className="w-full text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">Select Semester</option>
                {currentSemesters.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.semesterName || `Semester ${s.semesterNumber}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 🔥 FIX: Term Dropdown - Now shows when detected as term-based */}
          {systemTypeDetected && !isSemesterBased && (
            <div className="space-y-1.5 animate-in fade-in slide-in-from-left-2">
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                <FiClock className="w-3 h-3" /> Term
              </label>
              {currentTerms.length === 0 && (
                <div className="text-xs text-amber-600">No terms found</div>
              )}
              <select
                value={selectedTerm}
                onChange={(e) => safeSetSelectedTerm(e.target.value)}
                disabled={loading || currentTerms.length === 0}
                className="w-full text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">Select Term</option>
                {currentTerms.map(t => (
                  <option key={t._id || t.id} value={t._id || t.id}>
                    {t.termLabel || t.name || `Term ${t.termNumber}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            {enableReset && (
              <button
                onClick={resetSelections}
                disabled={loading}
                className="p-2.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
                title="Reset All"
              >
                <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            {!isViewOnly && isContextComplete && activeTab === 'active' && (
              <button
                onClick={handleCreateClick}
                disabled={loading || !hasExistingMapping}
                className="flex-1 bg-blue-600 text-white font-bold py-2 px-4 rounded-lg text-sm hover:bg-blue-700 shadow-sm transition-all whitespace-nowrap disabled:opacity-50"
                title={!hasExistingMapping ? 'Cannot create class: No mapping detected' : 'Create Class'}
              >
                Create Class
              </button>
            )}
          </div>
        </div>

        {/* System Type Indicator */}
        {systemTypeDetected && (
          <div className="mt-3 flex items-center">
            <span className={`text-xs px-2 py-1 rounded-full ${
              isSemesterBased 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-orange-100 text-orange-700'
            }`}>
              {isSemesterBased ? '📚 Semester System' : '📅 Term System'}
            </span>
          </div>
        )}

        {/* Mapping Status */}
        {showMappingStatus && isContextComplete && (
          <div className={`mt-4 p-3 rounded-lg border flex items-center gap-3 transition-colors ${
            loadingMapping ? 'bg-blue-50 border-blue-100' : 
            hasExistingMapping ? 'bg-green-50 border-green-100' : 'bg-amber-50 border-amber-100'
          }`}>
            {loadingMapping ? (
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent animate-spin rounded-full" />
            ) : hasExistingMapping ? (
              <FiCheckCircle className="text-green-600" />
            ) : (
              <FiAlertCircle className="text-amber-600" />
            )}
            <span className={`text-xs font-medium ${
              loadingMapping ? 'text-blue-700' : 
              hasExistingMapping ? 'text-green-700' : 'text-amber-700'
            }`}>
              {loadingMapping ? 'Validating mapping...' : 
               hasExistingMapping ? '✓ Configuration Valid: Mapping Found' : 
               '⚠ Configuration Warning: No Mapping Detected'}
            </span>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-100 text-red-700 text-xs rounded-lg flex items-center gap-2">
            <FiAlertCircle className="flex-shrink-0" /> {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicSelector;